import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import Sidebar from "../Sidebar";
import DefaultHeader from "../DefaultHeader";
import { useAuth } from "../../auth/context";
import AccountDialog from "../AccountDialog";
import { cx } from "../../utils";

/**
 * The admin shell: a fixed sidebar on desktop, a slide-over on mobile.
 *
 * It also gates the whole area — anyone without an admin session is bounced to
 * the login screen, with the page they wanted kept in location state so they
 * land back on it afterwards.
 */
const DefaultLayout = ({ children }) => {
  const { isAuthenticated, isTeam, user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  if (!isAuthenticated || !isTeam) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return (
    <div className="min-h-screen bg-ink-50">
      {/* Desktop sidebar */}
      <div className="fixed inset-y-0 left-0 z-40 hidden lg:block">
        <Sidebar />
      </div>

      {/* Mobile slide-over */}
      <div
        className={cx(
          "fixed inset-0 z-50 lg:hidden",
          open ? "pointer-events-auto" : "pointer-events-none",
        )}
      >
        <div
          className={cx(
            "absolute inset-0 bg-ink-950/50 transition-opacity duration-200",
            open ? "opacity-100" : "opacity-0",
          )}
          onClick={() => setOpen(false)}
        />
        <div
          className={cx(
            "absolute inset-y-0 left-0 transition-transform duration-200 ease-out",
            open ? "translate-x-0" : "-translate-x-full",
          )}
        >
          <Sidebar onNavigate={() => setOpen(false)} />
        </div>
      </div>

      <div className="lg:pl-64">
        <DefaultHeader onToggleSidebar={() => setOpen(true)} />
        {/* The support inbox is three panes side by side, so it gets the full
            width and less padding; every other page keeps the reading width. */}
        <main
          className={cx(
            "mx-auto w-full anim-in",
            location.pathname.startsWith("/support") ? "max-w-[110rem] px-5 py-5" : "max-w-6xl px-5 py-8",
          )}
        >
          {children}
        </main>
      </div>

      {/* Signed in with a temporary password: a new one first. */}
      {user?.mustChangePassword && <AccountDialog required />}
    </div>
  );
};

export default DefaultLayout;
